import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSsrBootstrap } from "@/lib/ssr-bootstrap";
import { useUser } from "@/hooks/useUser";
import {
  type AppMode,
  appModeStorageKey,
  canEnterHostMode,
  isGuestRoute as sharedIsGuestRoute,
  isHostRoute as sharedIsHostRoute,
  parseStoredMode,
  reconcileMode,
  resolveInitialMode,
} from "@workspace/access-control";

export type { AppMode } from "@workspace/access-control";

interface AppModeContextValue {
  mode: AppMode;
  setMode: (next: AppMode) => void;
  toggle: () => void;
  canHost: boolean;
}

const AppModeContext = createContext<AppModeContextValue | null>(null);

/**
 * Read the raw persisted value (or `null` when there is none / storage is
 * unavailable). Kept raw so `resolveInitialMode` can distinguish "no saved
 * preference" (→ hosts default to host view) from an explicit "guest".
 */
function readStoredModeRaw(userId: string | null): string | null {
  if (typeof window === "undefined") return null;
  const key = appModeStorageKey(userId);
  if (!key) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function readStoredMode(userId: string | null): AppMode {
  return parseStoredMode(readStoredModeRaw(userId));
}

function writeStoredMode(userId: string | null, mode: AppMode) {
  if (typeof window === "undefined") return;
  const key = appModeStorageKey(userId);
  if (!key) return;
  try {
    window.localStorage.setItem(key, mode);
  } catch {
    /* ignore quota / disabled storage */
  }
}

export function AppModeProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useUser();
  const userId = user?.id ?? null;
  const canHost = canEnterHostMode(profile);

  const ssrBootstrap = useSsrBootstrap();
  const [mode, setModeState] = useState<AppMode>(() => (ssrBootstrap ? "guest" : readStoredMode(userId)));
  const lastUserId = useRef<string | null>(userId);

  // Re-read the stored mode when the signed-in user changes (or when the
  // profile arrives after a fresh login) so each account gets its own
  // remembered hat. Non-hosts are pinned to guest regardless of storage,
  // and hosts with NO saved preference default to host view (delegated to
  // the shared `resolveInitialMode` — task #6266).
  useEffect(() => {
    const next = resolveInitialMode(readStoredModeRaw(userId), profile);
    setModeState(next);
    lastUserId.current = userId;
  }, [userId, profile]);

  const setMode = useCallback(
    (next: AppMode) => {
      const safe = reconcileMode(next, profile);
      setModeState(safe);
      writeStoredMode(userId, safe);
    },
    [userId, profile],
  );

  const toggle = useCallback(() => {
    if (!canHost) return;
    setMode(mode === "host" ? "guest" : "host");
  }, [mode, canHost, setMode]);

  const value = useMemo<AppModeContextValue>(
    () => ({ mode, setMode, toggle, canHost }),
    [mode, setMode, toggle, canHost],
  );

  return <AppModeContext.Provider value={value}>{children}</AppModeContext.Provider>;
}

export function useAppMode(): AppModeContextValue {
  const ctx = useContext(AppModeContext);
  if (!ctx) {
    throw new Error("useAppMode must be used within an AppModeProvider");
  }
  return ctx;
}

/**
 * Re-exported route classifiers. The implementations live in
 * `@workspace/access-control` so the mobile app's `AppModeProvider`
 * consumes the same predicates and can't drift.
 */
export const isGuestRoute = sharedIsGuestRoute;
export const isHostRoute = sharedIsHostRoute;
